import { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";

const locations = [
  { name: "Manchester", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/9f3ae319c_dinosaur-manchester-skyline-1186.webp", path: "/management/manchester" },
  { name: "London", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/519f0a96a_7.jpg", path: "/management/london" },
  { name: "Birmingham", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/54b1e7e77_Birmingham.jpg", path: "/management/birmingham" },
  { name: "Leeds", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/b933622a8_Leeds.jpg", path: "/management/leeds" },
  { name: "Sheffield", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/51258bf63_Sheffield.jpg", path: "/management/sheffield" },
  { name: "Brighton", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/66e8e9486_generated_image.png", path: "/management/brighton" },
  { name: "Liverpool", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/954ece74d_Liverpool.jpg", path: "/management/liverpool" },
  { name: "Chester", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/bd53abd39_Chester.jpg", path: "/management/chester" },
  { name: "Bath", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/96271565c_Bath.jpg", path: "/management/bath" },
  { name: "Oxford", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/7675b4d3d_generated_image.png", path: "/management/oxford" },
  { name: "Stockport", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/870370e5d_generated_image.png", path: "/management/stockport" },
  { name: "Southampton", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/8e00d35e9_Southampton.jpg", path: "/management/southampton" },
  { name: "Newcastle", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/3852708a2_Newcastle.jpg", path: "/management/newcastle" },
  { name: "Essex", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/2368d4f28_generated_image.png", path: "/management/essex" },
  { name: "Slough", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/4134daee7_generated_image.png", path: "/management/slough" },
  { name: "Stoke-on-Trent", image: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/7b53d04b6_StreoAd-260x2004.jpg", path: "/management/stoke-on-trent" },
];

const CARD_WIDTH = 240;
const CARD_GAP = 14;
const SPEED = 0.55;

export default function LocationsCarousel() {
  const trackRef = useRef(null);
  const posRef = useRef(0);
  const pausedRef = useRef(false);
  const rafRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startScrollRef = useRef(0);

  const items = [...locations, ...locations];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const totalWidth = locations.length * (CARD_WIDTH + CARD_GAP);
    const animate = () => {
      if (!pausedRef.current && !isDraggingRef.current) {
        posRef.current += SPEED;
        if (posRef.current >= totalWidth) posRef.current = 0;
        track.style.transform = `translateX(-${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const handleMouseDown = (e) => {
    isDraggingRef.current = true;
    pausedRef.current = true;
    startXRef.current = e.clientX;
    startScrollRef.current = trackRef.current?.scrollLeft || 0;
    trackRef.current?.style.setProperty('cursor', 'grabbing');
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    pausedRef.current = false;
    trackRef.current?.style.setProperty('cursor', 'grab');
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current || !trackRef.current) return;
    e.preventDefault();
    const delta = e.clientX - startXRef.current;
    trackRef.current.scrollLeft = startScrollRef.current - delta;
  };

  const handleMouseLeave = () => {
    if (isDraggingRef.current) {
      handleMouseUp();
    }
  };

  return (
    <section style={{
      backgroundColor: "#fff",
      paddingTop: "clamp(60px, 7vw, 96px)",
      paddingBottom: "clamp(60px, 7vw, 96px)",
    }}>
      {/* Header — same content width as other sections */}
      <div style={{ maxWidth: 1200, margin: "0 auto", paddingLeft: "clamp(20px, 5vw, 80px)", paddingRight: "clamp(20px, 5vw, 80px)", marginBottom: 40 }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>
          Our Locations
        </p>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#1A1A1A", margin: 0, marginBottom: 10 }}>
              Where we operate
            </h2>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#666", lineHeight: 1.6, margin: 0, maxWidth: 520 }}>
              From Manchester to London, we manage properties across the UK's most profitable short-let markets.
            </p>
          </div>
        </div>
      </div>

      {/* Carousel — full-width with complete fade to transparent on edges */}
      <div
        style={{ position: "relative" }}
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; setHoveredIndex(null); }}
      >
        {/* Full fade overlays — 180px wide fading completely to transparent */}
        <div className="loc-edge-fade" style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: 180, zIndex: 2,
          background: "linear-gradient(to right, #fff 0%, rgba(255,255,255,0) 100%)",
          pointerEvents: "none",
        }} />
        <div className="loc-edge-fade" style={{
          position: "absolute", right: 0, top: 0, bottom: 0, width: 180, zIndex: 2,
          background: "linear-gradient(to left, #fff 0%, rgba(255,255,255,0) 100%)",
          pointerEvents: "none",
        }} />

        {/* Extra vertical padding so hover shadow is not clipped */}
        <div
          style={{ overflow: "hidden", padding: "28px 0", cursor: "grab", userSelect: "none" }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <div ref={trackRef} style={{ display: "flex", gap: CARD_GAP, willChange: "transform" }}>
            {items.map(({ name, image, path }, i) => {
              const isHovered = hoveredIndex === i;
              return (
                <a
                  key={i}
                  href={path}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    flexShrink: 0,
                    width: CARD_WIDTH,
                    height: 320,
                    borderRadius: 16,
                    overflow: "hidden",
                    position: "relative",
                    display: "block",
                    textDecoration: "none",
                    cursor: "pointer",
                    transform: isHovered ? "rotate(-2deg) scale(1.06)" : "rotate(0deg) scale(1)",
                    boxShadow: isHovered ? "0 24px 56px rgba(0,0,0,0.28)" : "0 2px 12px rgba(0,0,0,0.08)",
                    transition: "transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease",
                    zIndex: isHovered ? 3 : 1,
                  }}
                >
                  <img src={image} alt={name === "Manchester" ? "Manchester city skyline with Kimpton Clocktower Hotel - Truestays Airbnb management" : `${name} cityscape - Truestays Airbnb management in ${name}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} loading="lazy" decoding="async" width="240" height="320" />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)" }} />
                  {/* Arrow icon top-right */}
                  <div style={{ position: "absolute", top: 14, right: 14, width: 30, height: 30, borderRadius: "50%", background: "rgba(255,255,255,0.15)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ArrowUpRight size={14} color="#fff" strokeWidth={2} />
                  </div>
                  <div style={{ position: "absolute", bottom: 18, left: 18, right: 18 }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 15, color: "#fff", letterSpacing: "-0.01em", display: "block", marginBottom: 4 }}>
                      {name}
                    </span>
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.65)", display: "block", lineHeight: 1.4 }}>
                      Expert Airbnb & short-let management in {name}
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .loc-edge-fade { display: none !important; }
        }
      `}</style>
    </section>
  );
}